import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Map } from "lucide-react";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { useAuthStore } from "@/store/auth";
import {
  EmptyState,
  HeaderCount,
  SectionCard,
  SectionToolbar,
  TableShell,
  Th,
} from "../components/ui";
import { CreateMpzpOrderModal } from "../components/mpzp/CreateMpzpOrderModal";
import { MpzpCoverageBadge } from "../components/mpzp/MpzpCoverageBadge";
import { MpzpDataGapsBar } from "../components/mpzp/MpzpDataGapsBar";
import { MpzpSelectionRulesPanel } from "../components/mpzp/MpzpSelectionRulesPanel";
import { MpzpStatusBadge } from "../components/mpzp/MpzpStatusBadge";
import { useMpzpCandidates, useMpzpOrders } from "../hooks";
import { fmtDate } from "../utils";
import type { MpzpCandidateGroup, MpzpOrderStatus } from "../types";

const STATUS_FILTERS: { value: "" | "active" | MpzpOrderStatus; label: string }[] = [
  { value: "active", label: "Aktywne" },
  { value: "", label: "Wszystkie" },
  { value: "fee_paid", label: "Do wysyłki" },
  { value: "sent", label: "Wysłane" },
  { value: "received", label: "Otrzymane" },
  { value: "completed", label: "Zakończone" },
];

/**
 * Hub modułu: po lewej działki do zamówienia pogrupowane po GMINIE (wypis z planu
 * wydaje urząd gminy, nie starostwo), po prawej zamówienia z ich statusem.
 *
 * Zaznaczenie startowe pochodzi z reguł liczonych na backendzie (pole `preselected`) —
 * front go nie wylicza, tylko pokazuje i pozwala zmienić.
 */
/* Zakładka „MPZP" strony „Zamawianie materiałów" (/power-projects/zamawianie?tab=mpzp). */
export function MpzpSection() {
  const me = useAuthStore((s) => s.user);
  // ?connection=… — wejście z rady na zakładce Działki projektu zawęża listę do tego
  // projektu; bez tego link obiecywałby filtr, którego strona nie stosuje.
  const [searchParams] = useSearchParams();
  const connectionFilter = searchParams.get("connection") ?? "";
  const { data: candidates, isLoading: candidatesLoading, refetch } = useMpzpCandidates(
    connectionFilter ? { connection: connectionFilter } : {},
  );
  const { data: orders, isLoading: ordersLoading } = useMpzpOrders();

  const groups = candidates?.groups ?? [];

  const [checked, setChecked] = useState<Record<string, boolean>>({});
  const [touched, setTouched] = useState(false);
  const [modalGroup, setModalGroup] = useState<MpzpCandidateGroup | null>(null);
  const [statusFilter, setStatusFilter] = useState<"" | "active" | MpzpOrderStatus>("active");
  const [mineOnly, setMineOnly] = useState(false);

  // Zaznaczenie startowe z reguł preselektu. Zasiewamy je RAZ — gdy użytkownik już
  // czegokolwiek dotknął, odświeżenie listy nie ma prawa cofnąć jego wyboru.
  useEffect(() => {
    if (touched || !groups.length) return;
    const seed: Record<string, boolean> = {};
    groups.forEach((g) => g.parcels.forEach((p) => { seed[p.id] = p.preselected; }));
    setChecked(seed);
  }, [groups, touched]);

  const paidCount = useMemo(
    () => (orders ?? []).filter((o) => o.status === "fee_paid").length,
    [orders],
  );

  const visibleOrders = useMemo(() => {
    let rows = orders ?? [];
    if (statusFilter === "active") {
      rows = rows.filter((o) => !["completed", "cancelled"].includes(o.status));
    } else if (statusFilter) {
      rows = rows.filter((o) => o.status === statusFilter);
    }
    // „Moje" = założone przeze mnie albo przejęte po nieaktywnym autorze.
    if (mineOnly && me?.id) {
      rows = rows.filter(
        (o) => String(o.created_by) === String(me.id) || String(o.taken_over_by) === String(me.id),
      );
    }
    return rows;
  }, [orders, statusFilter, mineOnly, me?.id]);

  const toggle = (id: string) => {
    setTouched(true);
    setChecked((s) => ({ ...s, [id]: !s[id] }));
  };

  const groupChecked = (group: MpzpCandidateGroup) => group.parcels.filter((p) => checked[p.id]);

  const toggleGroup = (group: MpzpCandidateGroup) => {
    const all = group.parcels.every((p) => checked[p.id]);
    setTouched(true);
    setChecked((s) => {
      const next = { ...s };
      group.parcels.forEach((p) => { next[p.id] = !all; });
      return next;
    });
  };

  return (
    <div className="space-y-4">
      <SectionToolbar
        text="Wypis i wyrys z planu miejscowego — urząd gminy/miasta, 1 wniosek = 1 gmina."
        actions={<HeaderCount count={paidCount} label="do wysyłki" tone="amber" />}
      />
      {connectionFilter && (
        <div className="flex items-center gap-2 text-xs border border-[var(--color-border)] bg-[var(--color-bg-elevated)] px-2 py-1.5">
          <span>Lista zawężona do jednego projektu.</span>
          <Link to="/power-projects/zamawianie?tab=mpzp" className="text-[var(--color-primary)] hover:underline">
            pokaż wszystkie działki →
          </Link>
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 items-start">
        {/* ── kandydaci ── */}
        <SectionCard title="Działki do zamówienia wypisu i wyrysu">
          {candidatesLoading ? (
            <div className="py-8 flex justify-center"><Spinner /></div>
          ) : !groups.length ? (
            <EmptyState>Brak działek poza aktywnymi zamówieniami MPZP</EmptyState>
          ) : (
            <div className="space-y-4">
              <MpzpSelectionRulesPanel
                rules={candidates?.selection_rules ?? []}
                legend={candidates?.coverage_legend ?? []}
              />
              <MpzpDataGapsBar projects={candidates?.projects ?? []} onDone={() => refetch()} />

              {groups.map((group) => {
                const selected = groupChecked(group);
                return (
                  <div key={group.commune_teryt || group.commune} className="space-y-1.5">
                    <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 border border-[var(--color-border)] bg-[var(--color-bg-elevated)] px-2.5 py-1.5">
                      <div className="flex flex-wrap items-center gap-2 min-w-0">
                        <span className="text-xs font-mono font-bold uppercase tracking-wide truncate">
                          Gmina {group.commune}
                          {group.county ? ` · ${group.county}` : ""}
                        </span>
                        <Badge color="gray">{group.parcel_count}</Badge>
                        {group.preselected_count > 0 && (
                          <Badge color="yellow">{group.preselected_count} wymagane</Badge>
                        )}
                        <label className="flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wide cursor-pointer text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] transition-colors">
                          <input
                            type="checkbox"
                            className="accent-[var(--color-primary)] cursor-pointer"
                            checked={group.parcels.every((p) => checked[p.id])}
                            onChange={() => toggleGroup(group)}
                          />
                          zaznacz wszystkie
                        </label>
                      </div>
                      <Button
                        size="sm"
                        disabled={selected.length === 0}
                        onClick={() => setModalGroup(group)}
                      >
                        <Map size={13} className="mr-1" />
                        Zamów wypis i wyrys ({selected.length})
                      </Button>
                    </div>
                    <TableShell>
                      <thead>
                        <tr>
                          <Th className="w-8"> </Th>
                          <Th className="whitespace-nowrap">Nr działki</Th>
                          <Th className="whitespace-nowrap">Obręb</Th>
                          <Th className="whitespace-nowrap">Plan</Th>
                          <Th className="whitespace-nowrap">Dlaczego</Th>
                          <Th className="w-full">Przedsięwzięcie</Th>
                        </tr>
                      </thead>
                      <tbody>
                        {group.parcels.map((p) => (
                          <tr
                            key={p.id}
                            className={`border-t border-[var(--color-border)] cursor-pointer transition-colors ${
                              checked[p.id]
                                ? "bg-[var(--color-primary-dim)]"
                                : "hover:bg-[var(--color-bg-elevated)]"
                            }`}
                            onClick={() => toggle(p.id)}
                          >
                            <td className="px-3 py-1.5 align-middle">
                              <input
                                type="checkbox"
                                className="accent-[var(--color-primary)] cursor-pointer"
                                checked={!!checked[p.id]}
                                readOnly
                              />
                            </td>
                            <td
                              className="px-3 py-1.5 align-middle font-mono font-bold whitespace-nowrap"
                              title={p.teryt}
                            >
                              {p.short_number || p.teryt}
                            </td>
                            <td className="px-3 py-1.5 align-middle font-mono whitespace-nowrap text-[var(--color-text-secondary)]">
                              {p.obreb || "—"}
                            </td>
                            <td className="px-3 py-1.5 align-middle whitespace-nowrap">
                              <MpzpCoverageBadge
                                status={p.mpzp_status}
                                label={p.mpzp_status_label}
                                coveredPct={p.mpzp_covered_pct}
                              />
                            </td>
                            <td className="px-3 py-1.5 align-middle">
                              {p.reasons.length ? (
                                <span className="flex flex-wrap gap-1">
                                  {p.reasons.map((r) => (
                                    <span key={r.key} title={r.detail}>
                                      <Badge color="blue">{r.label}</Badge>
                                    </span>
                                  ))}
                                </span>
                              ) : (
                                <span className="text-[var(--color-text-muted)]">—</span>
                              )}
                            </td>
                            <td className="px-3 py-1.5 align-middle">
                              <Link
                                to={`/power-projects/${p.project_id}?tab=parcels`}
                                className="block max-w-[26rem] line-clamp-2 leading-snug text-[var(--color-text-secondary)] hover:text-[var(--color-primary)] hover:underline"
                                title={p.project_name}
                                onClick={(e) => e.stopPropagation()}
                              >
                                {p.project_name}
                              </Link>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </TableShell>
                  </div>
                );
              })}
            </div>
          )}
        </SectionCard>

        {/* ── zamówienia ── */}
        <SectionCard
          title="Zamówienia wypisu i wyrysu"
          action={
            <div className="flex items-center gap-2">
              <label className="flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wide cursor-pointer text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] transition-colors">
                <input
                  type="checkbox"
                  className="accent-[var(--color-primary)] cursor-pointer"
                  checked={mineOnly}
                  onChange={() => setMineOnly((v) => !v)}
                />
                moje
              </label>
              <select
                className="input-base !py-0.5 text-xs"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}
              >
                {STATUS_FILTERS.map((f) => (
                  <option key={f.value} value={f.value}>{f.label}</option>
                ))}
              </select>
            </div>
          }
        >
          {ordersLoading ? (
            <div className="py-8 flex justify-center"><Spinner /></div>
          ) : !visibleOrders.length ? (
            <EmptyState>Brak zamówień</EmptyState>
          ) : (
            <TableShell>
              <thead>
                <tr>
                  <Th>Numer</Th>
                  <Th>Gmina</Th>
                  <Th>Zakres</Th>
                  <Th>Działki</Th>
                  <Th>Status</Th>
                  <Th>Zamawiający</Th>
                  <Th>Utworzono</Th>
                </tr>
              </thead>
              <tbody>
                {visibleOrders.map((o) => (
                  <tr
                    key={o.id}
                    className={`border-t border-[var(--color-border)] hover:bg-[var(--color-bg-elevated)] ${
                      o.status === "fee_paid" ? "bg-amber-50/60" : ""
                    }`}
                  >
                    <td className="px-3 py-1.5 font-mono">
                      <Link to={`/power-projects/mpzp/${o.id}`} className="hover:underline font-bold">
                        {o.number}
                      </Link>
                      {o.obtained_manually && (
                        <span
                          className="ml-1.5 text-[9px] uppercase tracking-wide px-1 py-px border border-[var(--color-border)] text-[var(--color-text-muted)]"
                          title="Wypis pozyskany ręcznie poza ELGW5 — dodany z karty działki samymi plikami, bez wniosku, opłat i wysyłki."
                        >
                          ręcznie
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-1.5">{o.commune}</td>
                    <td className="px-3 py-1.5">{o.scope_label}</td>
                    <td className="px-3 py-1.5" title={o.project_names.join(", ")}>
                      {o.parcel_count}
                    </td>
                    <td className="px-3 py-1.5"><MpzpStatusBadge status={o.status} /></td>
                    <td className="px-3 py-1.5">{o.created_by_name}</td>
                    <td className="px-3 py-1.5">{fmtDate(o.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </TableShell>
          )}
        </SectionCard>
      </div>

      {modalGroup && (
        <CreateMpzpOrderModal
          group={modalGroup}
          parcels={groupChecked(modalGroup)}
          onClose={() => setModalGroup(null)}
        />
      )}
    </div>
  );
}
